import { Controller } from '@hotwired/stimulus'

// Connects to data-controller="clic-atestiguado"
//
// Añade a los enlaces de partner la prueba de que el clic sale de alguien que está viendo esta
// página: un testigo firmado por el servidor (`ClicsAtestiguados::Testigo`).
//
//   página con enlaces a /redirections/  ──▶  POST /clics/testigo  ──▶  testigo en memoria
//   página que los pinta después          ──▶  igual, en cuanto aparece el primero
//   clic en uno de esos enlaces           ──▶  el href lleva rk_t solo durante ese clic
//
// Los enlaces pueden llegar después (los resultados de los asistentes, «ver más», streams de
// Turbo): se vigila el DOM en vez de pedir a cada componente que pinta HTML que avise.
//
// El HTML no cambia: el testigo se pone en el instante del clic y se quita en cuanto el navegador
// ha leído el destino, así que ni copiando URLs de la página ni con «copiar enlace» se obtiene.
// Cubre el clic normal, el de pestaña nueva (Ctrl/Cmd o rueda) y el teclado. «Abrir en pestaña
// nueva» desde el menú contextual no pasa por ningún evento y sale sin testigo.
//
// El testigo vive en el módulo y no en el controlador: la navegación dentro del frame `content`
// repinta el punto de montaje y reconecta el controlador, pero la pestaña es la misma y el testigo
// sigue valiendo. Sin JavaScript no hay testigo y el clic queda como `sin_prueba`; la redirección
// funciona igual.

const SELECTOR = 'a[href*="/redirections/"]'
const PARAMETRO = 'rk_t'
const BOTON_PRINCIPAL = 0
const BOTON_CENTRAL = 1
// El servidor da 24 h de vigencia. Se renueva antes para no usar uno a punto de caducar.
const RENOVAR_TRAS_MS = 23 * 60 * 60 * 1000

let testigo = null
let emitidoEn = 0
let pidiendo = false

const testigoAlDia = () => pidiendo || (testigo && Date.now() - emitidoEn < RENOVAR_TRAS_MS)

// Mira solo los nodos añadidos, no el documento entero: los cambios que no traen elementos (texto
// de cotizaciones, contadores) se descartan sin recorrer nada.
const traeEnlaces = (cambios) => cambios.some(({ addedNodes }) =>
  Array.from(addedNodes).some(nodo =>
    nodo.nodeType === Node.ELEMENT_NODE && (nodo.matches(SELECTOR) || nodo.querySelector(SELECTOR))))

export default class extends Controller {
  static values = {
    // Endpoint que emite el testigo.
    url: String
  }

  // Con el testigo ya al día, de una página anterior de la pestaña, no hay nada que pedir ni vigilar.
  connect () {
    if (testigoAlDia()) return
    if (document.querySelector(SELECTOR)) return this.pide()

    this.vigila()
  }

  disconnect () {
    this.vigilante?.disconnect()
  }

  vigila () {
    this.vigilante = new MutationObserver((cambios) => {
      if (!traeEnlaces(cambios)) return

      this.vigilante.disconnect()
      this.pide()
    })
    this.vigilante.observe(document.body, { childList: true, subtree: true })
  }

  // Sin cookies: emitir el testigo no necesita la sesión. Un fallo no se reintenta; sin testigo el
  // clic se cuenta igual, solo que como `sin_prueba`.
  pide () {
    if (testigoAlDia()) return
    pidiendo = true

    fetch(this.urlValue, { method: 'POST', credentials: 'omit', headers: { Accept: 'application/json' } })
      .then((respuesta) => (respuesta.ok ? respuesta.json() : null))
      .then((datos) => {
        if (!datos?.testigo) return
        testigo = datos.testigo
        emitidoEn = Date.now()
      })
      .catch(() => {})
      .finally(() => { pidiendo = false })
  }

  // Escucha `click` y `auxclick` en `document` y en fase de captura (`data-action` del punto de
  // montaje), para adelantarse al manejador de `turbo:click` de `hotwired.js`, que lee el href para
  // navegar. Se restaura en la siguiente vuelta del bucle de eventos, cuando el
  // navegador ya ha tomado el destino.
  firma (event) {
    const boton = event.type === 'click' ? BOTON_PRINCIPAL : BOTON_CENTRAL
    if (!testigo || event.button !== boton) return

    const enlace = event.target.closest?.(SELECTOR)
    if (!enlace) return

    const original = enlace.getAttribute('href')
    const destino = new URL(enlace.href)
    destino.searchParams.set(PARAMETRO, testigo)
    enlace.setAttribute('href', destino.toString())
    setTimeout(() => enlace.setAttribute('href', original), 0)
  }
}
